import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { formatFileSize } from '../../../../../../utils/fileSize';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../../../components/DataGrid';
import { FileTextIcon } from '../../../../../components/svgs/icons/FileTextIcon';
import { TrashIcon } from '../../../../../components/svgs/icons/TrashIcon';
import { Button } from '../../../../../components-v2/Button';
import { Pill } from '../../../../../components-v2/Pill';
import { styleSheet } from './styles';

const SOURCE_TYPE_LABELS: Record<Api.SharedFileOrigin, string> = {
	[Api.SharedFileOrigin.Unknown]: 'Unknown',
	[Api.SharedFileOrigin.Note]: 'Note',
	[Api.SharedFileOrigin.Survey]: 'Survey',
	[Api.SharedFileOrigin.FileRequest]: 'Secure Request',
	[Api.SharedFileOrigin.Automation]: 'Automation',
	[Api.SharedFileOrigin.Email]: 'Email',
	[Api.SharedFileOrigin.HtmlNewsletter]: 'Newsletter',
	[Api.SharedFileOrigin.TextMessage]: 'Text Message',
	[Api.SharedFileOrigin.SigningRequest]: 'E-Signature',
	[Api.SharedFileOrigin.Upload]: 'Upload',
	[Api.SharedFileOrigin.ClientPortal]: 'Client Portal',
};

function humanizeSourceType(sourceType?: Api.SharedFileOrigin): string {
	return SOURCE_TYPE_LABELS[sourceType] ?? sourceType ?? 'Note';
}

function formatDate(dateStr?: string): string {
	if (!dateStr) {
		return '';
	}
	return new Date(dateStr).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function getUploaderName(
	direction: Api.SharedFileDirection,
	creator?: Api.UserReference,
	contact?: Api.IContact
): string {
	if (direction === Api.SharedFileDirection.Received) {
		return [contact?.firstName, contact?.lastName].filter(Boolean).join(' ');
	}
	if (!creator) {
		return '';
	}
	return creator.fullName ?? [creator.firstName, creator.lastName].filter(Boolean).join(' ');
}

const DATA_CELL_STYLES = [styleSheet.dataGridCell, styleSheet.cellNoWrap, styleSheet.cellHiddenWhenStacked];

type IProps = {
	files: Api.ISharedFile[];
	variant: 'request' | 'received' | 'sent';
	contact?: Api.IContact;
	canDelete: boolean;
	downloadingFileId: string | null;
	onDownload: (file: Api.ISharedFile) => void;
	onDelete: (file: Api.ISharedFile) => void;
};

export const SharedFilesTable = ({
	files,
	variant,
	contact,
	canDelete,
	downloadingFileId,
	onDownload,
	onDelete,
}: IProps) => {
	const isSent = variant === 'sent';
	const isRequest = variant === 'request';
	return (
		<div className={css(styleSheet.dataGridWrapper, !isRequest && styleSheet.dataGridWrapperFlush)}>
			<DataGrid styles={[styleSheet.dataGrid]}>
				<DataGridColumns styles={[styleSheet.dataGridHeader]}>
					<DataGridColumn>Filename</DataGridColumn>
					<DataGridColumn>Date</DataGridColumn>
					{!isRequest && <DataGridColumn>{isSent ? 'Title' : 'Source'}</DataGridColumn>}
					{!isRequest && <DataGridColumn>{isSent ? 'Sender' : 'Uploader'}</DataGridColumn>}
					<DataGridColumn>Size</DataGridColumn>
					{canDelete && <DataGridColumn styles={[styleSheet.deleteColumn]} />}
				</DataGridColumns>
				<DataGridRows>
					{files.map(file => {
						const isDownloading = downloadingFileId === file.id;
						const date = formatDate(file.lastTransactionDate ?? file.creationDate);
						const sourceLabel = isRequest
							? undefined
							: isSent
								? file.origin?.sourceName
								: humanizeSourceType(file.origin?.sourceType);
						const personName = isRequest ? '' : getUploaderName(file.origin?.direction, file.creator, contact);
						return (
							<DataGridRow key={file.id} styles={[styleSheet.dataGridRow]}>
								<DataGridRowItem styles={[styleSheet.dataGridCell, styleSheet.cellFilename]}>
									<div className={css(styleSheet.filenameCell)}>
										<FileTextIcon width={20} height={20} className={css(styleSheet.fileIcon)} />
										<div className={css(styleSheet.fileDetails)}>
											<Button
												variant='link'
												disabled={isDownloading}
												onClick={() => onDownload(file)}
												title={`Download ${file.fileAttachment.fileName}`}
												styles={[styleSheet.filenameLink]}
											>
												<span className={css(styleSheet.filenameText)}>{file.fileAttachment.fileName}</span>
											</Button>
											<div className={css(styleSheet.fileMeta)}>
												<span className={css(styleSheet.fileMetaLine)}>
													{date}
													{sourceLabel && <span className={css(styleSheet.sourceBadge)}>{sourceLabel}</span>}
												</span>
												<span className={css(styleSheet.fileMetaLine)}>
													{[personName, formatFileSize(file.fileAttachment.fileSize)].filter(Boolean).join(' · ')}
												</span>
											</div>
										</div>
									</div>
								</DataGridRowItem>
								<DataGridRowItem styles={DATA_CELL_STYLES}>{date}</DataGridRowItem>
								{!isRequest && (
									<DataGridRowItem styles={DATA_CELL_STYLES}>
										{isSent ? sourceLabel : <Pill variant='label' color='gray' label={sourceLabel} size='small' />}
									</DataGridRowItem>
								)}
								{!isRequest && <DataGridRowItem styles={DATA_CELL_STYLES}>{personName}</DataGridRowItem>}
								<DataGridRowItem styles={DATA_CELL_STYLES}>
									{formatFileSize(file.fileAttachment.fileSize)}
								</DataGridRowItem>
								{canDelete && (
									<DataGridRowItem styles={[styleSheet.deleteColumn, styleSheet.dataGridCell, styleSheet.cellDelete]}>
										<Button
											variant='link'
											onClick={() => onDelete(file)}
											aria-label={`Delete ${file.fileAttachment.fileName}`}
										>
											<TrashIcon />
										</Button>
									</DataGridRowItem>
								)}
							</DataGridRow>
						);
					})}
				</DataGridRows>
			</DataGrid>
		</div>
	);
};
